import * as THREE from 'three';
import { SceneNode } from './SceneNode';
import { POI } from '../poi/POI';
import { UUID } from '../types';
import { Scene } from './Scene';

export type SceneChangeCallback = () => void;

/**
 * SceneNodeManager - Manages all scene nodes, POIs, and their lifecycle
 * Central hub for scene graph operations
 */
export class SceneNodeManager {
    private _rootNodes: SceneNode[] = [];
    private _pois: POI[] = [];
    private _nodeMap: Map<UUID, SceneNode> = new Map();
    private _isInitialized: boolean = false;
    private _scene: Scene;
    
    // Event callbacks
    private _sceneChangeCallbacks: SceneChangeCallback[] = [];
    
    // Frustum culling support
    private _lastFrustumCullingUpdate: number = 0;
    private _frustumCullingInterval: number = 1000 / 10; // Reduced to 10 FPS for stability (100ms)
    
    // Frustum culling statistics
    private _frustumCullingStats = {
        totalObjects: 0,
        visibleObjects: 0,
        culledObjects: 0,
        lastUpdateTime: 0
    };
    
    // Store previous frustum culling state to prevent unnecessary updates
    private _previousCullingState = new Map<string, boolean>();
    
    constructor(scene: Scene) {
        this._scene = scene;
    }
    
    /**
     * Initialize the scene manager
     */
    async initialize(): Promise<void> {
        if (this._isInitialized) {
            console.warn('SceneNodeManager is already initialized');
            return;
        }
        
        this._isInitialized = true;
        this.notifySceneChanged();
    }
    
    /**
     * Add a POI to the scene
     */
    addPOI(poi: POI): void {
        if (this._pois.includes(poi)) {
            console.warn('POI already exists in scene');
            return;
        }
        
        this._pois.push(poi);
        this.addSceneNode(poi);
        this.notifySceneChanged();
    }
    
    /**
     * Remove a POI from the scene
     */
    removePOI(poi: POI): void {
        const index = this._pois.indexOf(poi);
        if (index === -1) {
            console.warn('POI not found in scene');
            return;
        }
        
        this._pois.splice(index, 1);
        this.removeSceneNode(poi);
        this.notifySceneChanged();
    }
    
    /**
     * Get all POIs
     */
    getAllPOIs(): POI[] {
        return [...this._pois];
    }
    
    /**
     * Find POI by ID
     */
    findPOI(id: UUID): POI | null {
        return this._pois.find(poi => poi.id === id) || null;
    }
    
    /**
     * Add a scene node to the root level
     */
    addSceneNode(node: SceneNode): void {
        if (this._rootNodes.includes(node)) {
            console.warn('SceneNode already exists at root level');
            return;
        }
        
        this._rootNodes.push(node);
        this._nodeMap.set(node.id, node);

        // Also add the renderable THREE objects to the actual scene
        const threeObjects = node.getThreeObjects();
        threeObjects.forEach(obj => this._scene.add(obj));

        this.notifySceneChanged();
    }
    
    /**
     * Remove a scene node from the root level
     */
    removeSceneNode(node: SceneNode): void {
        const index = this._rootNodes.indexOf(node);
        if (index === -1) {
            console.warn('SceneNode not found at root level');
            return;
        }
        // First run node-specific cleanup (ensures labels/DOM/CSS3D are disposed)
        try {
            node.destroy();
        } catch (e) {
            console.warn('Error destroying SceneNode:', e);
        }

        // Remove any remaining THREE objects from the scene if still attached
        const threeObjects = node.getThreeObjects();
        threeObjects.forEach(obj => {
            // Detach from parent if needed
            if (obj.parent) {
                obj.parent.remove(obj);
            } else {
                // Fallback: try removing from our Scene wrapper
                try { this._scene.remove(obj); } catch {}
            }
        });

        // Remove from manager structures
        this._rootNodes.splice(index, 1);
        this._nodeMap.delete(node.id);

        this.notifySceneChanged();
    }
    
    /**
     * Get all root scene nodes
     */
    getAllRootNodes(): SceneNode[] {
        return [...this._rootNodes];
    }
    
    /**
     * Get all THREE.Object3D instances from managed nodes for raycasting.
     * This method recursively traverses the scene graph starting from root nodes.
     */
    public getVisibleThreeJSObjects(camera: THREE.Camera): THREE.Object3D[] {
        const objects: THREE.Object3D[] = [];
        for (const node of this._rootNodes) {
            this.collectThreeObjectsRecursive(node, objects);
        }
        return objects;
    }

    private collectThreeObjectsRecursive(node: SceneNode, objects: THREE.Object3D[]): void {
        if (!node.visible || !node.active) {
            return;
        }

        // Only add the main, visible mesh of a node to the raycast list.
        // This avoids picking up debug helpers or other internal objects.
        const mesh = (node as any)._mesh as THREE.Object3D;
        if (mesh) {
            objects.push(mesh);
        }

        // Recurse into children
        for (const child of node.getChildren()) {
            this.collectThreeObjectsRecursive(child, objects);
        }
    }
    
    /**
     * Find scene node by ID (recursive search)
     */
    findNode(id: UUID): SceneNode | null {
        return this._nodeMap.get(id) || null;
    }
    
    /**
     * Update all scene nodes
     */
    update(deltaTime: number): void {
        if (!this._isInitialized) return;
        
        // Update all root nodes (they will update their children)
        this._rootNodes.forEach(node => {
            if (node.active) {
                node.update(deltaTime);
            }
        });
        
        // Perform frustum culling at reduced frequency for performance
        this.updateFrustumCulling();
    }
    
    /**
     * Perform frustum culling on all scene nodes
     * This is called at a reduced frequency (10 FPS) for performance
     */
    private updateFrustumCulling(): void {
        const currentTime = performance.now();
        if (currentTime - this._lastFrustumCullingUpdate < this._frustumCullingInterval) {
            return; // Skip this frame
        }
        this._lastFrustumCullingUpdate = currentTime;
        
        // Get the camera from the scene
        const camera = this._scene.getCamera();
        if (!camera) return;
        
        // Check if frustum culling is enabled in app settings
        const appSettings = this._scene.getAppSettings();
        const isFrustumCullingEnabled = appSettings?.enableFrustumCulling?.value ?? false;
        
        if (!isFrustumCullingEnabled) {
            // Frustum culling is disabled - ensure all objects are visible
            this.restoreAllObjectsVisibility();
            return;
        }
        
        // Reset statistics for this frame
        this._frustumCullingStats.totalObjects = 0;
        this._frustumCullingStats.visibleObjects = 0;
        this._frustumCullingStats.culledObjects = 0;
        this._frustumCullingStats.lastUpdateTime = currentTime;
        
        // Perform culling on all root nodes (recursively handles children)
        this._rootNodes.forEach(node => {
            this.performFrustumCullingRecursive(node, camera);
        });
    }
    
    /**
     * Restore all objects to visible state when frustum culling is disabled
     */
    private restoreAllObjectsVisibility(): void {
        this._rootNodes.forEach(node => {
            this.restoreVisibilityRecursive(node);
        });
        // Clear the culling state since we're not using it
        this._previousCullingState.clear();
    }
    
    /**
     * Recursively restore visibility to all nodes
     */
    private restoreVisibilityRecursive(node: SceneNode): void {
        if (!node.active) return;
        
        // Restore visibility for mesh nodes
        const mesh3D = node as any;
        if (mesh3D._mesh) {
            const threeObject = mesh3D._mesh as THREE.Object3D;
            if (threeObject && !threeObject.visible && node.visible) {
                threeObject.visible = true;
                // //console.log(`👁️ Restored visibility: ${node.name}`);
            }
        }
        
        // Recursively restore children
        node.getChildren().forEach(child => {
            this.restoreVisibilityRecursive(child);
        });
    }
    
    /**
     * Recursively perform frustum culling on a node and its children
     */
    private performFrustumCullingRecursive(node: SceneNode, camera: THREE.Camera): void {
        // Skip inactive nodes
        if (!node.active) return;
        
        // Check if this node supports frustum culling (is a Mesh3D)
        const mesh3D = node as any;
        if (mesh3D.isInFrustum && typeof mesh3D.isInFrustum === 'function') {
            this._frustumCullingStats.totalObjects++;
            
            // No need to manually update bounding box - getBoundingBox() handles it automatically
            
            // Check if in frustum
            const isInFrustum = mesh3D.isInFrustum(camera);
            const shouldBeVisible = node.visible && isInFrustum;
            
            // Update statistics
            if (shouldBeVisible) {
                this._frustumCullingStats.visibleObjects++;
            } else {
                this._frustumCullingStats.culledObjects++;
            }
            
            // Only update THREE.js visibility if it actually changed from last check
            const nodeId = node.id;
            const previouslyVisible = this._previousCullingState.get(nodeId);
            
            if (previouslyVisible !== shouldBeVisible) {
                // State changed, update visibility
                const threeObject = (mesh3D as any)._mesh as THREE.Object3D;
                if (threeObject) {
                    threeObject.visible = shouldBeVisible;
                    this._previousCullingState.set(nodeId, shouldBeVisible);
                    
                    // Debug logging (can be removed for production)
                    if (!shouldBeVisible && node.visible) {
                        // //console.log(`🙈 Culled: ${node.name} (outside frustum)`);
                    } else if (shouldBeVisible && previouslyVisible === false) {
                        // //console.log(`👁️ Unculled: ${node.name} (back in frustum)`);
                    }
                }
            }
        }
        
        // Recursively check children
        node.getChildren().forEach(child => {
            this.performFrustumCullingRecursive(child, camera);
        });
    }
    
    /**
     * Get frustum culling statistics for debugging and performance monitoring
     */
    public getFrustumCullingStats(): {
        totalObjects: number;
        visibleObjects: number;
        culledObjects: number;
        cullRate: number;
        isEnabled: boolean;
        lastUpdateTime: number;
    } {
        const appSettings = this._scene.getAppSettings();
        const isEnabled = appSettings?.enableFrustumCulling?.value ?? false;
        const cullRate = this._frustumCullingStats.totalObjects > 0 ? 
            (this._frustumCullingStats.culledObjects / this._frustumCullingStats.totalObjects) * 100 : 0;
        
        return {
            ...this._frustumCullingStats,
            cullRate,
            isEnabled
        };
    }
    
    /**
     * Clear all scene nodes and POIs
     */
    clearAll(): void {
        // Clear all POIs (they extend SceneNode, so they'll be handled below)
        this._pois.forEach(poi => {
            // POIs will be disposed as part of root nodes
        });
        
        // Dispose all root nodes
        this._rootNodes.forEach(node => {
            // SceneNode doesn't have dispose method, just clear references
        });
        
        this._pois.length = 0;
        this._rootNodes.length = 0;
        this._nodeMap.clear();
        
        this.notifySceneChanged();
    }
    
    /**
     * Get scene statistics
     */
    getStats(): {
        totalNodes: number;
        totalPOIs: number;
        activeNodes: number;
        visibleNodes: number;
        frustumCulling: {
            totalObjects: number;
            visibleObjects: number;
            culledObjects: number;
            cullRate: number;
            isEnabled: boolean;
        };
    } {
        const activeNodes = this._rootNodes.filter(node => node.active).length;
        const visibleNodes = this._rootNodes.filter(node => node.visible).length;
        const frustumStats = this.getFrustumCullingStats();
        
        return {
            totalNodes: this._rootNodes.length,
            totalPOIs: this._pois.length,
            activeNodes,
            visibleNodes,
            frustumCulling: {
                totalObjects: frustumStats.totalObjects,
                visibleObjects: frustumStats.visibleObjects,
                culledObjects: frustumStats.culledObjects,
                cullRate: frustumStats.cullRate,
                isEnabled: frustumStats.isEnabled
            }
        };
    }
    
    /**
     * Add scene change callback
     */
    onSceneChanged(callback: SceneChangeCallback): void {
        this._sceneChangeCallbacks.push(callback);
    }
    
    /**
     * Remove scene change callback
     */
    offSceneChanged(callback: SceneChangeCallback): void {
        const index = this._sceneChangeCallbacks.indexOf(callback);
        if (index !== -1) {
            this._sceneChangeCallbacks.splice(index, 1);
        }
    }
    
    /**
     * Dispose the scene manager
     */
    dispose(): void {
        this.clearAll();
        this._sceneChangeCallbacks.length = 0;
        this._isInitialized = false;
    }
    
    // Private methods
    private notifySceneChanged(): void {
        this._sceneChangeCallbacks.forEach(callback => {
            try {
                callback();
            } catch (error) {
                console.error('Error in scene change callback:', error);
            }
        });
    }
} 